import React, { createContext, useContext, useEffect, useState, useRef } from "react";

type User = {
  id: string;
  name: string;
  micOn: boolean;
  videoOn: boolean;
};

type ChatMessage = {
  id: string;
  user: string;
  text: string;
  time: string;
};

type PendingUser = {
  id: string;
  name: string;
  micOn: boolean;
  videoOn: boolean;
};

type MeetContextType = {
  callActive: boolean;
  micOn: boolean;
  videoOn: boolean;
  isChatOpen: boolean;
  users: User[];
  chat: ChatMessage[];
  pendingUser: PendingUser | null;
  meJoined: boolean;
  shareOn: boolean;
  isUsersOpen: boolean;
  shareStream: MediaStream | null;

  startCall: () => void;
  endCall: () => void;
  toggleMic: () => void;
  toggleVideo: () => void;
  toggleChat: () => void;
  toggleShare: () => void;
  toggleUsers: () => void;

  acceptPending: () => void;
  rejectPending: () => void;
  sendMessage: (text: string) => void;
  joinMeeting: () => void;
};

const maleNames = ["Ayan", "Nursultan", "Daulet", "Alikhan", "Yerbol"];
const femaleNames = ["Aigerim", "Dana", "Zhanar", "Malika", "Aizada"];

const defaultChat = [
  {
    id: "1",
    user: 'Olzhas',
    text: "Опишите УТП проекта AIVOX?",
    time: "14:55:01"
  },
  {
    id: "2",
    user: 'Rinat',
    text: "Платформа, которая автоматически фиксирует решения, превращает переговоры в структурированные данные и формирует корпоративную базу знаний",
    time: "14:55:43"
  },
]

const defaultUsers = [
  {
    id: "1",
    name: "Olzhas",
    micOn: false,
    videoOn: false,
  },
  {
    id: "2",
    name: "Bekzat",
    micOn: false,
    videoOn: false,
  },
  {
    id: "3",
    name: "Rinat",
    micOn: false,
    videoOn: false,
  }, // TODO
]

const MeetContext = createContext<MeetContextType | null>(null);

export const MeetProvider = ({ children }: { children: React.ReactNode }) => {
  const [callActive, setCallActive] = useState(false); //TODO
  const [micOn, setMicOn] = useState(true);
  const [videoOn, setVideoOn] = useState(true);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [users, setUsers] = useState<User[]>([
    ...defaultUsers
  ]);
  const [chat, setChat] = useState<ChatMessage[]>([
    ...defaultChat
  ]);
  const [pendingUser, setPendingUser] = useState<PendingUser | null>(null);
  const [meJoined, setMeJoined] = useState(false); //TODO
  const [shareOn, setShareOn] = useState(false);
  const [shareStream, setShareStream] = useState<MediaStream | null>(null);
  const [isUsersOpen, setIsUsersOpen] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!callActive) return;

    timerRef.current = setTimeout(createPendingUser, 5000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [callActive]);

  const createPendingUser = () => {
    if (pendingUser) return;

    const isMale = Math.random() > 0.5;
    const namePool = isMale ? maleNames : femaleNames;
    const randomName = namePool[Math.floor(Math.random() * namePool.length)];

    const randomLetter = String.fromCharCode(65 + Math.floor(Math.random() * 26)); // A-Z

    const newPending: PendingUser = {
      id: crypto.randomUUID(),
      name: `${randomName} ${randomLetter}.`,
      micOn: Math.random() > 0.5,
      videoOn: Math.random() > 0.5,
    };

    setPendingUser(newPending);
  };

  const scheduleNextPending = () => {
    if (!callActive) return;
    const nextDelay = Math.floor(Math.random() * (180 - 5) + 5) * 1000;
    timerRef.current = setTimeout(createPendingUser, nextDelay);
  };

  const acceptPending = () => {
    if (!pendingUser) return;
    setUsers((prev) => [
      ...prev,
      {
        id: pendingUser.id,
        name: pendingUser.name,
        micOn: pendingUser.micOn,
        videoOn: pendingUser.videoOn,
      },
    ]);
    setPendingUser(null);
    scheduleNextPending();
  };

  const rejectPending = () => {
    setPendingUser(null);
    scheduleNextPending();
  };

  const startCall = () => {
    setCallActive(true);

  };

  const endCall = () => {
    setCallActive(false);
    setUsers(defaultUsers);
    setChat(defaultChat);
    setPendingUser(null);
    setMeJoined(false);
    setShareOn(false);
    setShareStream(null);
    setMicOn(false);
    setVideoOn(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  const toggleMic = () => setMicOn((prev) => !prev);
  const toggleVideo = () => setVideoOn((prev) => !prev);
  const toggleChat = () => setIsChatOpen((prev) => !prev);
  const toggleUsers = () => setIsUsersOpen((prev) => !prev);

  const toggleShare = () => {
    if (!shareOn) {
      navigator.mediaDevices.getDisplayMedia({ video: true, audio: false })
        .then((stream) => {
          setShareStream(stream);
          setShareOn(true);

          const track = stream.getVideoTracks()[0];
          track.onended = () => {
            setShareOn(false);
            setShareStream(null);
          };

          console.log("Экран расшаривается");
        })
        .catch((err) => {
          console.error("Ошибка доступа к экрану:", err);
          setShareOn(false);
          setShareStream(null);
        });
    } else {
      if (shareStream) {
        shareStream.getTracks().forEach((t) => t.stop());
      }
      setShareStream(null);
      setShareOn(false);
      console.log("Шаринг экрана остановлен");
    }
  };

  const sendMessage = (text: string) => {
    if (!text.trim()) return;
    setChat((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        user: "me",
        text,
        time: new Date().toLocaleTimeString(),
      },
    ]);
  };

  const joinMeeting = () => {
    setMeJoined(true);
    setUsers([...users, { id: "me", name: "Азамат А.", micOn, videoOn }]);
  };

  return (
    <MeetContext.Provider
      value={{
        callActive,
        micOn,
        videoOn,
        isChatOpen,
        users,
        chat,
        pendingUser,
        startCall,
        endCall,
        toggleMic,
        toggleVideo,
        toggleChat,
        acceptPending,
        rejectPending,
        sendMessage,
        meJoined,
        joinMeeting,
        shareOn,
        toggleShare,
        isUsersOpen,
        toggleUsers,
        shareStream,

      }}
    >
      {children}
    </MeetContext.Provider>
  );
};

export const useMeet = () => {
  const ctx = useContext(MeetContext);
  if (!ctx) throw new Error("useMeet must be used inside MeetProvider");
  return ctx;
};
